import { css } from 'lit';

/**
 * Shared CSS for Live Calls Monitor components.
 * Extracted from live-calls-monitor.js following Option C pattern.
 */
export const liveCallsStyles = css`
  :host {
    display: flex;
    flex-direction: column;
    /* 32px outer card offset + 2px app-layout border, 4rem content padding */
    height: calc(100vh - 34px - 4rem);
    overflow: hidden;
  }

  /* ─── Page Header ─── */
  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--space-8);
    flex-shrink: 0;
  }

  .page-header-text {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }

  .page-title {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--text-4xl);
    font-weight: var(--font-black);
    color: var(--color-text-primary);
    letter-spacing: -0.04em;
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .page-description {
    margin: 0;
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-tertiary);
  }

  /* ─── Split Layout ─── */
  .monitor-layout {
    display: grid;
    grid-template-columns: 360px 1fr;
    flex: 1;
    min-height: 0;
    gap: 0;
    background: transparent;
    border-radius: var(--radius-xl);
    overflow: hidden;
    border: 1px solid var(--color-border-primary);
    box-shadow: var(--shadow-md);
  }

  /* ─── Left Panel ─── */
  .left-panel {
    display: flex;
    flex-direction: column;
    background: rgba(255, 255, 255, 0.18);
    backdrop-filter: blur(8px) saturate(1.2);
    -webkit-backdrop-filter: blur(8px) saturate(1.2);
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-right: 1px solid rgba(255, 255, 255, 0.35);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
    position: relative;
    overflow: hidden;
  }

  .left-header {
    padding: var(--space-4) var(--space-5);
    border-bottom: 1px solid var(--color-border-primary);
    flex-shrink: 0;
  }

  .connection-dot {
    width: 8px;
    height: 8px;
    border-radius: var(--radius-full);
    flex-shrink: 0;
  }

  .connection-dot.connected {
    background: var(--color-success);
    animation: softPulse 2.5s ease-in-out infinite;
  }

  .connection-dot.disconnected {
    background: var(--color-text-disabled);
  }

  /* ─── Filter Tabs ─── */
  .filter-tabs {
    display: flex;
    gap: var(--space-1);
    margin-bottom: var(--space-3);
  }

  .filter-tab {
    flex: 1;
    padding: var(--space-2) var(--space-2);
    background: transparent;
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-md);
    color: var(--color-text-tertiary);
    font-family: var(--font-body);
    font-size: var(--text-xs);
    font-weight: var(--font-medium);
    cursor: pointer;
    transition: all var(--transition-fast);
    text-align: center;
    white-space: nowrap;
  }

  .filter-tab:hover {
    color: var(--color-text-secondary);
    border-color: var(--color-border-secondary);
  }

  .filter-tab.active {
    background: var(--color-primary);
    border-color: var(--color-primary);
    color: var(--color-text-inverse);
    font-weight: var(--font-semibold);
  }

  .filter-tab .tab-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 4px;
    border-radius: var(--radius-full);
    font-size: 10px;
    font-weight: var(--font-bold);
    margin-left: 4px;
  }

  .filter-tab.active .tab-count {
    background: rgba(255, 255, 255, 0.25);
    color: var(--color-text-inverse);
  }

  .filter-tab:not(.active) .tab-count {
    background: var(--color-bg-tertiary);
    color: var(--color-text-tertiary);
  }

  /* ─── Search ─── */
  .search-box {
    position: relative;
  }

  .search-box input {
    width: 100%;
    padding: var(--space-2) var(--space-3) var(--space-2) var(--space-10);
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-md);
    color: var(--color-text-primary);
    font-family: var(--font-body);
    font-size: var(--text-sm);
    transition: all var(--transition-fast);
    box-sizing: border-box;
  }

  .search-box input:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: var(--shadow-glow-primary);
  }

  .search-box input::placeholder {
    color: var(--color-text-tertiary);
  }

  .search-icon {
    position: absolute;
    left: var(--space-3);
    top: 50%;
    transform: translateY(-50%);
    color: var(--color-text-tertiary);
    font-size: var(--text-sm);
    pointer-events: none;
  }

  /* ─── Call Cards List ─── */
  .call-list {
    flex: 1;
    overflow-y: auto;
    padding: var(--space-3);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .call-card {
    background: rgba(255, 255, 255, 0.18);
    backdrop-filter: blur(8px) saturate(1.2);
    -webkit-backdrop-filter: blur(8px) saturate(1.2);
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-left: 3px solid transparent;
    border-radius: var(--radius-md);
    padding: var(--space-4);
    cursor: pointer;
    transition: all var(--transition-fast);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
    position: relative;
    overflow: hidden;
  }

  .call-card:hover {
    border-color: var(--color-border-secondary);
    border-left-color: var(--color-border-secondary);
    background: var(--color-bg-hover);
  }

  .call-card.selected {
    border-color: var(--color-primary);
    border-left-color: var(--color-primary);
    background: var(--color-primary-light);
    box-shadow: var(--shadow-glow-primary);
  }

  .call-card-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--space-2);
  }

  .call-card-phone {
    font-family: var(--font-mono);
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    color: var(--color-text-primary);
  }

  .call-card-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: var(--radius-full);
    font-family: var(--font-body);
    font-size: 10px;
    font-weight: var(--font-semibold);
  }

  .call-card-badge.active {
    background: var(--color-success-bg);
    color: var(--color-success-text);
  }

  .call-card-badge.active .badge-dot {
    width: 6px;
    height: 6px;
    border-radius: var(--radius-full);
    background: var(--color-success);
    animation: softPulse 2.5s ease-in-out infinite;
  }

  .call-card-meta {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    font-family: var(--font-body);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
  }

  .call-card-meta-item {
    display: flex;
    align-items: center;
    gap: var(--space-1);
  }

  .call-card-meta-item i {
    font-size: 11px;
  }

  .call-card-duration {
    font-family: var(--font-mono);
    font-weight: var(--font-medium);
    color: var(--color-text-secondary);
  }

  /* ─── Right Panel ─── */
  .right-panel {
    display: flex;
    flex-direction: column;
    background: rgba(255, 255, 255, 0.18);
    backdrop-filter: blur(8px) saturate(1.2);
    -webkit-backdrop-filter: blur(8px) saturate(1.2);
    border: 1px solid rgba(255, 255, 255, 0.35);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
    position: relative;
    overflow: hidden;
  }

  /* ─── Detail Header ─── */
  .detail-header {
    padding: var(--space-5) var(--space-6);
    border-bottom: 1px solid var(--color-border-primary);
    flex-shrink: 0;
  }

  .detail-header-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--space-3);
  }

  .detail-phone {
    font-family: var(--font-display);
    font-size: var(--text-xl);
    font-weight: var(--font-bold);
    color: var(--color-text-primary);
    letter-spacing: var(--tracking-tight);
  }

  .detail-live-badge {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-1) var(--space-3);
    background: var(--color-success-bg);
    border-radius: var(--radius-full);
    font-family: var(--font-body);
    font-size: var(--text-xs);
    font-weight: var(--font-semibold);
    color: var(--color-success-text);
  }

  .detail-live-dot {
    width: 7px;
    height: 7px;
    border-radius: var(--radius-full);
    background: var(--color-success);
    animation: softPulse 2.5s ease-in-out infinite;
  }

  .detail-meta {
    display: flex;
    align-items: center;
    gap: var(--space-6);
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-tertiary);
  }

  .detail-meta-item {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }

  .detail-meta-item i {
    font-size: var(--text-sm);
    width: 16px;
    text-align: center;
  }

  .detail-meta-value {
    color: var(--color-text-secondary);
    font-weight: var(--font-medium);
  }

  .detail-meta-value.mono {
    font-family: var(--font-mono);
    font-size: var(--text-xs);
  }

  /* ─── Transcript ─── */
  .transcript-area {
    flex: 1;
    overflow-y: auto;
    padding: var(--space-5) var(--space-6);
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .transcript-message {
    display: flex;
    flex-direction: column;
    max-width: 75%;
    animation: slideUp var(--duration-base) var(--ease-out) both;
  }

  .transcript-message.ai {
    align-self: flex-start;
  }

  .transcript-message.caller {
    align-self: flex-end;
  }

  .transcript-bubble {
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-lg);
    font-family: var(--font-body);
    font-size: var(--text-sm);
    line-height: var(--leading-relaxed);
    position: relative;
    box-shadow: var(--shadow-xs);
  }

  .transcript-message.ai .transcript-bubble {
    background: var(--color-accent-subtle);
    color: var(--color-text-primary);
    border-bottom-left-radius: var(--radius-sm);
  }

  .transcript-message.caller .transcript-bubble {
    background: var(--color-bg-tertiary);
    color: var(--color-text-primary);
    border-bottom-right-radius: var(--radius-sm);
  }

  .transcript-label {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    font-weight: var(--font-semibold);
    color: var(--color-text-tertiary);
    margin-bottom: var(--space-1);
    text-transform: uppercase;
    letter-spacing: var(--tracking-wider);
  }

  .transcript-message.ai .transcript-label {
    color: var(--color-accent);
  }

  .transcript-time {
    font-family: var(--font-mono);
    font-size: 10px;
    color: var(--color-text-tertiary);
    margin-top: var(--space-1);
  }

  .transcript-message.ai .transcript-time {
    text-align: left;
  }

  .transcript-message.caller .transcript-time {
    text-align: right;
  }

  /* ─── Actions Bar ─── */
  .actions-bar {
    padding: var(--space-4) var(--space-6);
    border-top: 1px solid rgba(255, 255, 255, 0.35);
    display: flex;
    align-items: center;
    gap: var(--space-3);
    flex-shrink: 0;
    background: rgba(255, 255, 255, 0.18);
    backdrop-filter: blur(8px) saturate(1.2);
    -webkit-backdrop-filter: blur(8px) saturate(1.2);
    border: 1px solid rgba(255, 255, 255, 0.35);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
    position: relative;
    overflow: hidden;
  }

  .action-btn {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-4);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-md);
    background: var(--color-bg-secondary);
    font-family: var(--font-body);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: all var(--transition-fast);
    white-space: nowrap;
  }

  .action-btn:hover {
    background: var(--color-bg-tertiary);
    color: var(--color-text-primary);
    border-color: var(--color-border-secondary);
  }

  .action-btn.primary {
    background: var(--color-primary);
    border-color: var(--color-primary);
    color: var(--color-text-inverse);
  }

  .action-btn.primary:hover {
    background: var(--color-primary-hover);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }

  .action-btn.danger {
    color: var(--color-error);
  }

  .action-btn.danger:hover {
    background: var(--color-error-light);
    border-color: var(--color-error);
  }

  .action-btn i {
    font-size: var(--text-sm);
  }

  .actions-spacer {
    flex: 1;
  }

  .action-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .action-btn:disabled:hover {
    background: var(--color-bg-secondary);
    color: var(--color-text-secondary);
    border-color: var(--color-border-primary);
    transform: none;
    box-shadow: none;
  }

  .action-btn.active {
    background: var(--color-primary-light);
    border-color: var(--color-primary);
    color: var(--color-primary);
  }

  .action-btn.flagged {
    background: #FEF3C7;
    border-color: #D97706;
    color: #D97706;
    text-transform: capitalize;
  }

  /* ─── Flag Button Wrapper ─── */
  .flag-btn-wrap {
    position: relative;
  }

  .call-card-badges {
    display: flex;
    align-items: center;
    gap: var(--space-1);
  }

  .call-card-badge.flag-important {
    background: #FEF3C7;
    color: #D97706;
  }

  .call-card-badge.flag-followup {
    background: #DBEAFE;
    color: #2563EB;
  }

  .call-card-badge.flag-issue {
    background: #FEE2E2;
    color: #DC2626;
  }

  .call-card-badge.flag-spam {
    background: #F3F4F6;
    color: #6B7280;
  }

  /* ─── Flag Menu ─── */
  .flag-menu {
    position: absolute;
    bottom: calc(100% + 6px);
    right: 0;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-lg);
    padding: var(--space-2);
    min-width: 160px;
    z-index: 100;
  }

  .flag-menu-item {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    width: 100%;
    padding: var(--space-2) var(--space-3);
    border: none;
    background: transparent;
    border-radius: var(--radius-md);
    font-family: var(--font-body);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: background var(--transition-fast);
    text-align: left;
  }

  .flag-menu-item:hover {
    background: var(--color-bg-tertiary);
    color: var(--color-text-primary);
  }

  .flag-menu-item.flag-remove {
    color: var(--color-text-tertiary);
  }

  .flag-menu-divider {
    height: 1px;
    background: var(--color-border-primary);
    margin: var(--space-1) 0;
  }

  /* ─── Notes Panel ─── */
  .notes-panel {
    border-top: 1px solid var(--color-border-primary);
    background: var(--color-bg-secondary);
    display: flex;
    flex-direction: column;
    max-height: 280px;
    flex-shrink: 0;
  }

  .notes-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-3) var(--space-4);
    border-bottom: 1px solid var(--color-border-primary);
  }

  .notes-title {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    color: var(--color-text-primary);
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }

  .notes-close {
    background: transparent;
    border: none;
    color: var(--color-text-tertiary);
    cursor: pointer;
    padding: var(--space-1);
    border-radius: var(--radius-sm);
    font-size: var(--text-sm);
  }

  .notes-close:hover {
    color: var(--color-text-primary);
    background: var(--color-bg-tertiary);
  }

  .notes-list {
    flex: 1;
    overflow-y: auto;
    padding: var(--space-3) var(--space-4);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .notes-loading,
  .notes-empty {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-tertiary);
    text-align: center;
    padding: var(--space-4);
  }

  .note-item {
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-md);
    padding: var(--space-3);
  }

  .note-content {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-primary);
    line-height: var(--leading-relaxed);
    margin-bottom: var(--space-2);
  }

  .note-meta {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    font-family: var(--font-body);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
  }

  .note-author {
    font-weight: var(--font-medium);
  }

  .note-delete {
    margin-left: auto;
    background: transparent;
    border: none;
    color: var(--color-text-disabled);
    cursor: pointer;
    padding: 2px 4px;
    border-radius: var(--radius-sm);
    font-size: var(--text-xs);
  }

  .note-delete:hover {
    color: var(--color-error);
    background: var(--color-error-light);
  }

  .notes-input-row {
    display: flex;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-4);
    border-top: 1px solid var(--color-border-primary);
  }

  .notes-input {
    flex: 1;
    padding: var(--space-2) var(--space-3);
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-md);
    color: var(--color-text-primary);
    font-family: var(--font-body);
    font-size: var(--text-sm);
    box-sizing: border-box;
  }

  .notes-input:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: var(--shadow-glow-primary);
  }

  .notes-input::placeholder {
    color: var(--color-text-tertiary);
  }

  .notes-add-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: var(--color-primary);
    border: none;
    border-radius: var(--radius-md);
    color: var(--color-text-inverse);
    cursor: pointer;
    transition: background var(--transition-fast);
    flex-shrink: 0;
  }

  .notes-add-btn:hover:not(:disabled) {
    background: var(--color-primary-hover);
  }

  .notes-add-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* ─── Hang Up Button ─── */
  .hangup-btn {
    background: #DC2626 !important;
    border-color: #DC2626 !important;
    color: #fff !important;
    font-weight: 600;
  }

  .hangup-btn:hover:not(:disabled) {
    background: #B91C1C !important;
    border-color: #B91C1C !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.35);
  }

  .hangup-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }

  /* Confirm dialog overlay now handled by <voice-modal> component.
     .confirm-icon kept for slotted body content. */
  .confirm-icon {
    width: 48px;
    height: 48px;
    border-radius: var(--radius-full);
    background: var(--color-error-light);
    color: var(--color-error);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--text-xl);
    margin: 0 auto var(--space-4);
  }

  /* btn-danger — uses global from componentStyles */

  .btn-danger:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* ─── Empty State ─── */
  .empty-panel {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: var(--space-8);
    text-align: center;
  }

  .empty-icon {
    width: 72px;
    height: 72px;
    border-radius: var(--radius-xl);
    background: var(--color-bg-tertiary);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.75rem;
    color: var(--color-text-disabled);
    margin-bottom: var(--space-5);
  }

  .empty-title {
    font-family: var(--font-display);
    font-size: var(--text-lg);
    font-weight: var(--font-semibold);
    color: var(--color-text-secondary);
    margin-bottom: var(--space-2);
  }

  .empty-desc {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-tertiary);
    max-width: 280px;
    line-height: var(--leading-relaxed);
  }

  /* ─── Responsive ─── */
  @media (max-width: 1024px) {
    .monitor-layout {
      grid-template-columns: 1fr;
      height: auto;
    }

    .left-panel {
      border-right: none;
      border-bottom: 1px solid var(--color-border-primary);
      max-height: 400px;
    }

    .right-panel {
      min-height: 500px;
    }
  }

  @media (max-width: 640px) {
    .left-panel {
      max-height: 320px;
    }

    .filter-tabs {
      flex-wrap: wrap;
    }

    .actions-bar {
      flex-wrap: wrap;
    }
  }

  /* ─── Pipeline State Badges ─── */

  .detail-badges {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .pipeline-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    white-space: nowrap;
    transition: all 0.3s ease;
  }

  .pipeline-badge i {
    font-size: 0.65rem;
  }

  .pipeline-compact {
    margin-top: 6px;
    padding: 2px 8px;
    font-size: 0.65rem;
  }

  .pipeline-compact i {
    font-size: 0.6rem;
  }

  /* Listening — gray/neutral */
  .pipeline-listening {
    background: rgba(168, 162, 158, 0.12);
    color: #78716C;
  }

  /* Processing/Thinking — warm amber */
  .pipeline-processing {
    background: rgba(217, 119, 6, 0.10);
    color: #B45309;
  }

  /* Speaking — blue */
  .pipeline-speaking {
    background: rgba(37, 99, 235, 0.10);
    color: #1D4ED8;
  }

  /* Tool execution — teal/green */
  .pipeline-tool {
    background: rgba(45, 143, 111, 0.10);
    color: #1A7A5A;
  }

  /* Animated dots */
  .pipeline-dots {
    display: inline-flex;
    gap: 2px;
    margin-left: 2px;
  }

  .pipeline-dot {
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.4;
  }

  /* Listening — no animation (just static dots, faded) */
  .pipeline-listening .pipeline-dots {
    display: none;
  }

  /* Processing — pulsing dots */
  .pipeline-processing .pipeline-dot {
    animation: pipelinePulse 1.2s ease-in-out infinite;
  }
  .pipeline-processing .pipeline-dot:nth-child(2) {
    animation-delay: 0.2s;
  }
  .pipeline-processing .pipeline-dot:nth-child(3) {
    animation-delay: 0.4s;
  }

  /* Speaking — wave animation */
  .pipeline-speaking .pipeline-dot {
    animation: pipelineWave 1s ease-in-out infinite;
  }
  .pipeline-speaking .pipeline-dot:nth-child(2) {
    animation-delay: 0.15s;
  }
  .pipeline-speaking .pipeline-dot:nth-child(3) {
    animation-delay: 0.3s;
  }

  /* Tool — spinning/pulsing */
  .pipeline-tool .pipeline-dot {
    animation: pipelineSpin 0.8s ease-in-out infinite;
  }
  .pipeline-tool .pipeline-dot:nth-child(2) {
    animation-delay: 0.15s;
  }
  .pipeline-tool .pipeline-dot:nth-child(3) {
    animation-delay: 0.3s;
  }

  @keyframes pipelinePulse {
    0%, 100% { opacity: 0.3; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.4); }
  }

  @keyframes pipelineWave {
    0%, 100% { opacity: 0.3; transform: translateY(0); }
    50% { opacity: 1; transform: translateY(-3px); }
  }

  @keyframes pipelineSpin {
    0%, 100% { opacity: 0.3; transform: scale(0.8); }
    50% { opacity: 1; transform: scale(1.5); }
  }
`;
